<section class="relative bg-gradient-to-br from-gray-900 to-blue-900 text-white overflow-hidden">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 left-0 w-1/3 h-1/3 bg-blue-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-0 right-0 w-1/3 h-1/3 bg-indigo-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 py-24 md:py-32 relative z-10">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div class="text-center lg:text-left">
        <div class="inline-block bg-blue-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
          <span class="text-sm font-medium text-blue-300">
            <i class="bx bx-hdd mr-2"></i>IIS Volume Storage
          </span>
        </div>
        
        <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
          High-Performance <span class="text-blue-400">Volume Storage</span>
        </h1>
        
        <p class="text-xl text-gray-300 mb-8 max-w-2xl mx-auto lg:mx-0">
          Enterprise-grade block storage volumes optimized for IIS applications with high IOPS, low latency, and instant scalability for your critical workloads.
        </p>
        
        <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
          <a routerLink="/contact" class="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl flex items-center justify-center">
            <i class="bx bx-plus-circle mr-2 text-lg"></i>
            Create Volume
          </a>
          <a routerLink="/pricing" class="bg-transparent border border-blue-400 text-blue-200 hover:bg-blue-500/10 font-semibold py-3 px-8 rounded-lg transition-all duration-300 flex items-center justify-center">
            <i class="bx bx-dollar mr-2 text-lg"></i>
            View Pricing
          </a>
        </div>
        
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl mx-auto lg:mx-0 text-left">
          <div class="bg-white/5 border border-blue-500/20 rounded-xl px-4 py-3">
            <div class="text-lg font-bold text-blue-300">50,000+</div>
            <div class="text-xs text-gray-300 uppercase tracking-wide">IOPS</div>
          </div>
          <div class="bg-white/5 border border-blue-500/20 rounded-xl px-4 py-3">
            <div class="text-lg font-bold text-blue-300">&lt;1 ms</div>
            <div class="text-xs text-gray-300 uppercase tracking-wide">Latency</div>
          </div>
          <div class="bg-white/5 border border-blue-500/20 rounded-xl px-4 py-3">
            <div class="text-lg font-bold text-blue-300">64 TB</div>
            <div class="text-xs text-gray-300 uppercase tracking-wide">Per Volume</div>
          </div>
          <div class="bg-white/5 border border-blue-500/20 rounded-xl px-4 py-3">
            <div class="text-lg font-bold text-blue-300">Multi‑AZ</div>
            <div class="text-xs text-gray-300 uppercase tracking-wide">Redundancy</div>
          </div>
        </div>
      </div>
      
      <div class="relative">
        <div class="relative z-10 rounded-2xl overflow-hidden shadow-2xl border border-gray-700">
          <img src="assets/img/volume-storage-hero.png" alt="IIS Volume Storage" class="w-full h-auto object-cover">
        </div>
        
        <div class="absolute -bottom-6 -left-6 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-5 shadow-xl w-40">
          <div class="text-2xl font-bold">High</div>
          <div class="text-sm">Performance</div>
        </div>
        
        <div class="absolute -top-6 -right-6 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl p-5 shadow-xl w-40">
          <div class="text-2xl font-bold">Volume</div>
          <div class="text-sm">Storage</div>
        </div>
      </div>
    </div>
  </div>
</section>

<section class="py-20 bg-slate-900/90 text-white">
  <div class="container mx-auto px-4">
    <div class="text-center mb-14">
      <div class="inline-block bg-blue-500/10 text-blue-300 px-4 py-1 rounded-full text-sm font-medium mb-4 border border-blue-500/40">
        Volume Profiles
      </div>
      <h2 class="text-3xl md:text-4xl font-bold mb-4">
        Volume Types for <span class="text-blue-400">Every IIS Workload</span>
      </h2>
      <p class="text-lg text-slate-300 max-w-3xl mx-auto">
        Choose from performance, balanced, and capacity-optimized volumes to match your application profile and budget.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
      <div class="bg-slate-800/70 border border-blue-500/30 rounded-2xl p-8 shadow-xl">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold">Performance SSD</h3>
          <span class="px-3 py-1 text-xs rounded-full bg-blue-500/20 text-blue-200 border border-blue-400/60">Recommended</span>
        </div>
        <p class="text-slate-300 mb-4">
          NVMe-backed volumes for latency-sensitive IIS workloads and high-traffic websites.
        </p>
        <ul class="space-y-2 text-sm text-slate-200 mb-6">
          <li>• IOPS: Up to 50,000</li>
          <li>• Throughput: Up to 1,000 MB/s</li>
          <li>• Ideal for: APIs, e-commerce, transactional systems</li>
        </ul>
        <div class="flex justify-between text-xs text-slate-300">
          <div>
            <div class="font-semibold">Capacity</div>
            <div>100 GB – 10 TB</div>
          </div>
          <div>
            <div class="font-semibold">Use Case</div>
            <div>Prod IIS</div>
          </div>
        </div>
      </div>

      <div class="bg-slate-800/60 border border-slate-600 rounded-2xl p-8 shadow-xl">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold">Balanced SSD</h3>
          <span class="px-3 py-1 text-xs rounded-full bg-emerald-500/10 text-emerald-200 border border-emerald-400/60">Most Popular</span>
        </div>
        <p class="text-slate-300 mb-4">
          General-purpose volumes balancing performance and cost for typical IIS applications.
        </p>
        <ul class="space-y-2 text-sm text-slate-200 mb-6">
          <li>• IOPS: Up to 15,000</li>
          <li>• Throughput: Up to 500 MB/s</li>
          <li>• Ideal for: Web apps, line-of-business, staging</li>
        </ul>
        <div class="flex justify-between text-xs text-slate-300">
          <div>
            <div class="font-semibold">Capacity</div>
            <div>50 GB – 8 TB</div>
          </div>
          <div>
            <div class="font-semibold">Use Case</div>
            <div>Prod / UAT</div>
          </div>
        </div>
      </div>

      <div class="bg-slate-800/60 border border-slate-600 rounded-2xl p-8 shadow-xl">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold">Capacity HDD</h3>
          <span class="px-3 py-1 text-xs rounded-full bg-slate-500/20 text-slate-200 border border-slate-400/60">Capacity</span>
        </div>
        <p class="text-slate-300 mb-4">
          Cost-optimized HDD volumes for logs, archives, and less frequently accessed data.
        </p>
        <ul class="space-y-2 text-sm text-slate-200 mb-6">
          <li>• IOPS: Up to 3,000</li>
          <li>• Throughput: Up to 250 MB/s</li>
          <li>• Ideal for: Logs, backups, test environments</li>
        </ul>
        <div class="flex justify-between text-xs text-slate-300">
          <div>
            <div class="font-semibold">Capacity</div>
            <div>500 GB – 64 TB</div>
          </div>
          <div>
            <div class="font-semibold">Use Case</div>
            <div>Archive / DR</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<section class="py-20 bg-gray-50">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-blue-100 text-blue-800 px-4 py-1 rounded-full text-sm font-medium mb-4">
        Volume Features
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
        Enterprise <span class="text-blue-600">Volume Storage</span>
      </h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        High-performance block storage optimized for IIS applications with enterprise-grade features.
      </p>
    </div>
    
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-6">
          <i class="bx bx-tachometer text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">High Performance</h3>
        <p class="text-gray-600 mb-4">
          Up to 50,000 IOPS with consistent low-latency performance for IIS applications.
        </p>
        <div class="flex items-center text-sm text-gray-500">
          <i class="bx bx-check-circle text-green-500 mr-2"></i>
          <span>Optimized for OLTP, APIs, and latency-sensitive workloads.</span>
        </div>
      </div>
      
      <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6">
          <i class="bx bx-expand text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">Instant Scalability</h3>
        <p class="text-gray-600 mb-4">
          Scale storage capacity instantly from 1GB to 64TB without downtime.
        </p>
        <div class="flex items-center text-sm text-gray-500">
          <i class="bx bx-check-circle text-green-500 mr-2"></i>
          <span>Resize volumes online as your IIS workloads grow.</span>
        </div>
      </div>
      
      <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center mb-6">
          <i class="bx bx-shield-quarter text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">Enterprise Security</h3>
        <p class="text-gray-600 mb-4">
          Encrypted at rest with automatic snapshots and point-in-time recovery.
        </p>
        <div class="flex items-center text-sm text-gray-500">
          <i class="bx bx-check-circle text-green-500 mr-2"></i>
          <span>Meets strict compliance and security requirements.</span>
        </div>
      </div>
      
      <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center mb-6">
          <i class="bx bx-link text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">Multi-Attach</h3>
        <p class="text-gray-600 mb-4">
          Attach volumes to multiple instances for shared storage scenarios.
        </p>
        <div class="flex items-center text-sm text-gray-500">
          <i class="bx bx-check-circle text-green-500 mr-2"></i>
          <span>Ideal for clustered IIS, shared configs, and file shares.</span>
        </div>
      </div>
      
      <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center mb-6">
          <i class="bx bx-time text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">Snapshot Ready</h3>
        <p class="text-gray-600 mb-4">
          Create point-in-time snapshots for backup and disaster recovery.
        </p>
        <div class="flex items-center text-sm text-gray-500">
          <i class="bx bx-check-circle text-green-500 mr-2"></i>
          <span>Automate backups and integrate with recovery workflows.</span>
        </div>
      </div>
      
      <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl flex items-center justify-center mb-6">
          <i class="bx bx-money text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">Cost Effective</h3>
        <p class="text-gray-600 mb-4">
          Pay only for what you use with no upfront costs or long-term commitments.
        </p>
        <div class="flex items-center text-sm text-gray-500">
          <i class="bx bx-check-circle text-green-500 mr-2"></i>
          <span>Predictable, usage-based pricing aligned with your growth.</span>
        </div>
      </div>
    </div>
  </div>
</section>
